import { CommonModule } from '@angular/common'
import { Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import {
  IAutoQuestionAutomationFormData,
  IAutoQuestionFormData,
} from '@employer/app/components/jobs-automation/automation-auto-question/auto-questions.model'
import { AutomationAutoQuestionFormComponent } from '@employer/app/components/jobs-automation/automation-auto-question/automation-auto-question-form.component'

import { PermissionStore } from '@engineering11/access-web'
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button'
import { E11ChipModule } from '@engineering11/ui-lib/e11-chip'
import { E11DividerModule } from '@engineering11/ui-lib/e11-divider'
import { E11DynamicFormModule, E11FormForType } from '@engineering11/ui-lib/e11-form-builder'
import { E11InputLabelModule } from '@engineering11/ui-lib/e11-input-label'
import { E11NotificationsService } from '@engineering11/ui-lib/e11-notifications'
import { ISelectOption } from '@engineering11/ui-lib/e11-select'
import { E11SwitchModule } from '@engineering11/ui-lib/e11-switch'
import { E11Logger } from '@engineering11/web-api-error'
import { TranslateModule } from '@ngx-translate/core'
import { combineLatest, map } from 'rxjs'
import { APPLICATION_STATE, ConfigStore, CultureIndexPermissions, FormSelectService, SharedLibModule } from 'shared-lib'
import { IApplicationAutoResponseFormData } from './auto-response.model'
import { AutomationAutoResponseFormComponent } from './automation-auto-response/automation-auto-response-form.component'
import { AutomationCandidateRejectionFormComponent } from './automation-candidate-rejection/automation-candidate-rejection-form.component'
import { IRejectCandidateFormData, rejectCandidateDefaults } from './automation-candidate-rejection/candidate-rejection.model'
import { AutomationCultureIndexFormComponent } from './automation-culture-index/automation-culture-index-form.component'
import { IAutomationFormConfig } from './automation-form-message'
import { IAutomationFormData } from './automation-form.model'
import { IRequestMoreFormData, IRequestUpdatesFormData } from './automation-request-updates/auto-request-updates.model'
import { AutomationRequestUpdatesSectionComponent } from './automation-request-updates/automation-request-updates-form.component'
import { AutomationSwimlaneChangeService } from './automation-swimlane-change.service'
import { AutomationCandidateUnresponsiveFormComponent } from './automation-unresponsive-candidate/automation-candidate-unresponsive-form.component'
import { IUnresponsiveCandidateFormData, unresponsiveCandidateDefaults } from './automation-unresponsive-candidate/unresponsive-candidate.model'
import { ICultureIndexFormData } from './culture-index.model'
import { ISendMessageFormData } from './job-automation.model'
import { AutomationTypeDisplayNames } from './partials/automation-upgrade-prompt.component'
import { SwimlaneFlowHeaderComponent } from './partials/swimlane-flow-header.component'
export interface IAutomationFormFields {
  autoResponse: E11FormForType<ISendMessageFormData>
  requestedUpdate: E11FormForType<IRequestUpdatesFormData>
  requestMoreInitial: E11FormForType<ISendMessageFormData>
  requestMoreFollowUp: E11FormForType<ISendMessageFormData>
  requestMoreWarning: E11FormForType<ISendMessageFormData>
  cultureIndexMessage?: E11FormForType<ISendMessageFormData>
  cultureIndexFollowUpMessage?: E11FormForType<ISendMessageFormData>
  cultureIndexWarningMessage?: E11FormForType<ISendMessageFormData>
}

const SWIMLANE_SELECTABLE_OPTIONS = [APPLICATION_STATE.SCREENED, APPLICATION_STATE.INTERVIEWING, APPLICATION_STATE.FINALIST]
const DO_NOTHING_OPTION: ISelectOption<APPLICATION_STATE | null> = { name: 'Do nothing', value: null }

@Component({
  selector: 'job-automation-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    E11DynamicFormModule,
    E11InputLabelModule,
    E11SwitchModule,
    TranslateModule,
    E11ButtonModule,
    E11DividerModule,
    AutomationAutoQuestionFormComponent,
    AutomationCandidateUnresponsiveFormComponent,
    AutomationCandidateRejectionFormComponent,
    SwimlaneFlowHeaderComponent,
    SharedLibModule,
    E11ChipModule,
    AutomationRequestUpdatesSectionComponent,
    AutomationCultureIndexFormComponent,
    AutomationAutoResponseFormComponent,
  ],
  template: `
    <ng-container>
      <div class="e11-flex e11-justify-between e11-items-center e11-py-0.5">
        <h3>{{ formHeader }}</h3>
        <e11-button
          *ngIf="canSave"
          value="{{ 'Save changes' | translate }}"
          [size]="'sm'"
          [color]="'primary-accent'"
          [type]="'button'"
          [loading]="saving"
          [disabled]="saving || loading"
          [loadingText]="'Saving...'"
          (click)="!saving && saveClicked()"
        >
        </e11-button>
      </div>
      <e11-divider [margin]="1"></e11-divider>
      <div class="e11-flex e11-flex-col e11-gap-4">
        @if(!loading){
        <swimlane-flow-header />}
        <e11-divider [margin]="1"></e11-divider>

        <automation-auto-response-form
          #autoResponseFormRef
          [loading]="loading"
          [showEnableSwitch]="showEnableSwitch"
          [showResetButton]="showResetButton"
          [formConfig]="_formConfig"
          [initialData]="formData.autoResponse"
        />

        <e11-divider [margin]="1"></e11-divider>
        <automation-culture-index-form
          #cultureIndexFormRef
          [loading]="loading"
          [showEnableSwitch]="showEnableSwitch"
          [showResetButton]="showResetButton"
          [formConfig]="_formConfig"
          [initialData]="formData.cultureIndex"
          [swimlaneOptions]="swimlaneOptions"
        />

        <automation-request-updates-section
          #requestUpdatesFormRef
          [loading]="loading"
          [showEnableSwitch]="showEnableSwitch"
          [showResetButton]="showResetButton"
          [formConfig]="_formConfig"
          [initialData]="formData.requestMore"
          [swimlaneOptions]="swimlaneOptions"
        ></automation-request-updates-section>

        <e11-divider [margin]="1"></e11-divider>

        <div *ngIf="showAutoQuestionForm">
          <job-automation-auto-question-form
            [loading]="loading"
            [formData]="formData.autoQuestions"
            [formConfig]="_formConfig"
            (valueChanges)="autoQuestions = $event"
            (saveQuestionsChanges)="saveAutoQuestions(formData, $event)"
          ></job-automation-auto-question-form>
          <e11-divider [margin]="1"></e11-divider>
        </div>

        <div *ngIf="showUnresponsiveCandidateForm">
          <job-automation-candidate-unresponsive-form
            [loading]="loading"
            [formData]="formData.unresponsiveCandidate"
            (valueChanges)="unresponsiveCandidate = $event"
          ></job-automation-candidate-unresponsive-form>
          <e11-divider [margin]="1"></e11-divider>
        </div>

        <div>
          <job-automation-candidate-rejection-form
            *ngIf="showRejectionCandidateForm"
            [loading]="loading"
            [formData]="formData.rejectCandidate"
            (valueChanges)="rejectCandidate = $event"
          ></job-automation-candidate-rejection-form>
        </div>
      </div>
    </ng-container>
  `,
})
export class JobAutomationFormComponent {
  @ViewChild('autoResponseFormRef') autoResponseFormRef!: AutomationAutoResponseFormComponent
  @ViewChild('cultureIndexFormRef') cultureIndexFormRef!: AutomationCultureIndexFormComponent
  @ViewChild('requestUpdatesFormRef') requestUpdatesFormRef!: AutomationRequestUpdatesSectionComponent

  @Input() saving = false
  @Input() loading = false
  @Input() showResetButton = false
  @Input() showEnableSwitch = false
  @Input() showAutoQuestionForm = false
  @Input() showUnresponsiveCandidateForm = false
  @Input() showRejectionCandidateForm = false
  @Input() canSave = true
  @Input() formHeader = 'Manage Automation'
  @Input() formData: IAutomationFormData = {} as IAutomationFormData
  _formConfig?: IAutomationFormConfig
  @Input() set formConfig(config: IAutomationFormConfig) {
    this._formConfig = { ...config }
  }

  @Output() newFormData = new EventEmitter<IAutomationFormData>()

  autoQuestions?: IAutoQuestionAutomationFormData
  unresponsiveCandidate?: IUnresponsiveCandidateFormData
  rejectCandidate?: IRejectCandidateFormData
  AutomationTypeDisplayNames = AutomationTypeDisplayNames

  #logger = inject(E11Logger)

  constructor(
    private formSelectService: FormSelectService,
    private notification: E11NotificationsService,
    private configStore: ConfigStore,
    private permissionStore: PermissionStore,
    public swimlaneChangeService: AutomationSwimlaneChangeService
  ) {}

  features$ = this.configStore.features$
  canManageCultureIndex$ = combineLatest([this.features$, this.permissionStore.hasPermission(CultureIndexPermissions.ManageCultureIndex)]).pipe(
    map(([features, hasPermission]) => features?.cultureIndex && hasPermission)
  )
  swimlaneOptions = [
    DO_NOTHING_OPTION,
    ...this.formSelectService.getApplicationStateOptions().filter(s => SWIMLANE_SELECTABLE_OPTIONS.includes(s.value)),
  ]

  private combineChanges<T>(value1: T, value2: Partial<T> | undefined): T {
    return {
      ...value1,
      ...value2,
    }
  }

  validateForms() {
    if (this.autoResponseFormRef && !this.autoResponseFormRef.validate()) {
      return false
    }

    if (this.cultureIndexFormRef && !this.cultureIndexFormRef.validate()) {
      return false
    }

    if (this.requestUpdatesFormRef && !this.requestUpdatesFormRef.validate()) {
      return false
    }
    return true
  }

  async saveClicked() {
    if (!this.validateForms()) {
      this.notification.popNotificationMessage({
        message: 'Please ensure all fields are filled out correctly',
        type: 'error',
        autoClose: true,
        title: 'Could not save automation',
        dismissOnRouteChange: true,
      })
      return
    }

    const autoResponse: IApplicationAutoResponseFormData = this.autoResponseFormRef?.value
    const requestMore: IRequestMoreFormData = this.requestUpdatesFormRef?.value
    const cultureIndex: ICultureIndexFormData | undefined = this.cultureIndexFormRef?.value
    const autoQuestions: IAutoQuestionAutomationFormData = this.combineChanges(this.formData.autoQuestions, this.autoQuestions)
    const unresponsiveCandidate: IUnresponsiveCandidateFormData = this.combineChanges(unresponsiveCandidateDefaults, this.unresponsiveCandidate)
    const rejectCandidate: IRejectCandidateFormData = this.combineChanges(rejectCandidateDefaults, this.rejectCandidate)

    const formData: IAutomationFormData = {
      requestMore,
      autoResponse,
      autoQuestions,
      unresponsiveCandidate,
      rejectCandidate,
      cultureIndex,
    }

    this.#logger.log('JobAutomationFormComponent.saveClicked', formData)
    this.newFormData.emit(formData)
  }

  /**
   * Saves ONLY the questions changes made from the autoQuestion modal
   */
  saveAutoQuestions(existingFormData: IAutomationFormData, autoQuestions: IAutoQuestionFormData[]) {
    this.newFormData.emit({
      ...existingFormData,
      autoQuestions: { enabled: existingFormData.autoQuestions.enabled, autoQuestions },
    })
  }
}

/**
 * Options:
 * 1: Save will only save if all forms pass
 * 2: Save saves the passing forms and not the failing forms?
 * 3: Save saves all forms?
 */
